import { useState, useEffect, useRef } from 'react';
import './Explore.css';
import Navigation from '../Navigation/Navigation';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Text, VStack, Avatar, HStack, Icon, Box, Spinner } from '@chakra-ui/react';
import { FaHeart } from 'react-icons/fa';
import { FiArrowRight, FiBookOpen } from 'react-icons/fi';
import { useOutletContext } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

import { backendURL } from '../../App';

const MotionBox = motion(Box);

const FadeInCard = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </MotionBox>
  );
};

const StoryCard = ({ story, onClick, onAuthorClick, index }) => {
  const preview = story.chapters && story.chapters.length > 0
    ? story.chapters[0].content.substring(0, 150) + '...'
    : 'No preview available';

  const chapterCount = story.chapters ? story.chapters.length : 0;

  return (
    <FadeInCard delay={Math.min(index * 0.06, 0.3)}>
      <div className="story-card" onClick={() => onClick(story.id)}>
        <div className="story-card-header">
          <h2>{story.title}</h2>
          <div className="story-meta">
            <Avatar size="xs" name={story.author} bg="#E05252" color="white" />
            <span
              className="story-author"
              onClick={(e) => {
                e.stopPropagation();
                onAuthorClick(story.author);
              }}
              style={{ cursor: 'pointer' }}
            >
              {story.author}
            </span>
            <span className="story-date">{story.published_date}</span>
          </div>
        </div>
        <p className="story-preview">{preview}</p>
        <div className="story-card-footer">
          <div className="story-stats">
            <span className="story-likes">
              <Icon as={FaHeart} color="#E05252" boxSize={3} /> {story.likes}
            </span>
          </div>
          <span className="story-chapter-count">
            {chapterCount} {chapterCount === 1 ? 'chapter' : 'chapters'}
          </span>
          <span className="read-indicator">
            Read <Icon as={FiArrowRight} boxSize={3.5} />
          </span>
        </div>
      </div>
    </FadeInCard>
  );
};

const Explore = ({ isAuthorView = false }) => {
  const navigate = useNavigate();
  const { authorName } = useParams();
  const [stories, setStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { onLogOut, isAuthenticated } = useOutletContext();


  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`${backendURL}/explore/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setStories(data);
      } catch (error) {
        console.error('Error fetching stories:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStories();
  }, []);

  const handleStoryClick = (storyId) => {
    navigate(`/story/${storyId}`);
  };

  const handleAuthorClick = (author) => {
    navigate(`/author/${encodeURIComponent(author)}`);
  };

  const filteredStories = isAuthorView && authorName
    ? stories.filter(story => story.author === decodeURIComponent(authorName))
    : stories;

  if (isLoading) {
    return (
      <Box bg="#F5F3F0" minH="100vh">
        {isAuthenticated && (
          <Navigation
            bg="#F5F3F0"
            onRouteChange={(route) => navigate(`/${route}`)}
            onLogOut={onLogOut}
            isAuthenticated={isAuthenticated}
          />
        )}
        <div className="explore-container">
          <VStack spacing={8} justify="center" align="center" h="50vh">
            <Spinner size="xl" color="#E05252" thickness="3px" />
            <Text color="#52525B" fontSize="lg">Loading stories...</Text>
          </VStack>
        </div>
      </Box>
    );
  }

  if (error) {
    if (error.includes('Failed to fetch')) {
      return (
        <Box bg="#F5F3F0" minH="100vh">
          {isAuthenticated && (
            <Navigation
              bg="#F5F3F0"
              onRouteChange={(route) => navigate(`/${route}`)}
              onLogOut={onLogOut}
              isAuthenticated={isAuthenticated}
            />
          )}
          <div className="explore-container">
          </div>
        </Box>
      );
    }
    return (
      <Box bg="#F5F3F0" minH="100vh">
        {isAuthenticated && (
          <Navigation
            bg="#F5F3F0"
            onRouteChange={(route) => navigate(`/${route}`)}
            onLogOut={onLogOut}
            isAuthenticated={isAuthenticated}
          />
        )}
        <div className="explore-container">
          <div className="error">
            Error loading stories: {error}
          </div>
        </div>
      </Box>
    );
  }

  return (
    <Box bg="#F5F3F0" minH="100vh">
      {isAuthenticated && (
        <Navigation
          bg="#F5F3F0"
          onRouteChange={(route) => navigate(`/${route}`)}
          onLogOut={onLogOut}
          isAuthenticated={isAuthenticated}
        />
      )}
      <div className="explore-container">
        <MotionBox
          className="explore-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <VStack spacing={3}>
            {isAuthorView ? (
              <>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  color="#E05252"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                >
                  Author
                </Text>
                <Heading
                  fontWeight="800"
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="#1A1A1A"
                  letterSpacing="-0.03em"
                >
                  {decodeURIComponent(authorName)}
                </Heading>
                <Text fontSize="md" color="#52525B" maxW="480px">
                  Explore all stories by this author
                </Text>
              </>
            ) : (
              <>
                <HStack spacing={2} mb={1}>
                  <Icon as={FiBookOpen} color="#E05252" boxSize={4} />
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="#E05252"
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    Community Stories
                  </Text>
                </HStack>
                <Heading
                  fontWeight="800"
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="#1A1A1A"
                  letterSpacing="-0.03em"
                >
                  Explore Stories
                </Heading>
                <Text
                  fontSize="md"
                  color="#52525B"
                  maxW="480px"
                >
                  Discover what others have written with Writewizard
                </Text>
              </>
            )}
          </VStack>
        </MotionBox>

        <div className="stories-grid">
          {filteredStories.length > 0 ? (
            filteredStories.map((story, index) => (
              <StoryCard
                key={story.id}
                story={story}
                onClick={handleStoryClick}
                onAuthorClick={handleAuthorClick}
                index={index}
              />
            ))
          ) : (
            <div className="no-stories">
              {isAuthorView
                ? `No stories found by ${decodeURIComponent(authorName)}`
                : 'No stories published yet'
              }
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

export default Explore;
